import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useCompany } from "@/config";
import { PageHeader, Kpi, Section, StatusBadge, HealthBadge, healthFromPct, fmtTime } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ClockWidget from "@/components/ClockWidget";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const COLORS = ["#1F4E8C", "#FF5500", "#2E6CB5", "#FF884D", "#77A3D6", "#FFBB99"];

const ChartBox = ({ empty, height, id, children }) => (
  empty ? (
    <div style={{ height }} className="flex items-center justify-center text-sm text-neutral-400" data-testid={`chart-empty-${id}`}>
      No timesheet hours logged in this period
    </div>
  ) : (
    <div data-testid={`chart-${id}`}>
      <ResponsiveContainer width="100%" height={height}>{children}</ResponsiveContainer>
    </div>
  )
);


export default function AdminDashboard() {
  const company = useCompany();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [charts, setCharts] = useState({ daily_trend: [], employee_hours: [], project_hours: [], utilization: [] });
  const [activity, setActivity] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pending, setPending] = useState({ pending_count: 0, total_active: 0, week_start: "", week_end: "", pending: [] });
  const [approvals, setApprovals] = useState({ total: 0, leave: 0, timesheets: 0 });
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [days, setDays] = useState(30);
  const [nudging, setNudging] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  const load = async () => {
    try {
      const [s, c, a, p, pl, ap] = await Promise.all([
        api.get("/dashboard/admin"),
        api.get(`/dashboard/charts?include_completed=${includeCompleted}&days=${days}`),
        api.get(`/dashboard/daily-activity?date=${date}`),
        api.get("/projects"),
        api.get("/timesheets/weeks/pending-last"),
        api.get("/approvals/pending-count"),
      ]);
      setStats(s.data); setCharts(c.data); setActivity(a.data); setProjects(p.data); setPending(pl.data); setApprovals(ap.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, includeCompleted, days]);

  const nudge = async () => {
    if (!window.confirm(`Send a reminder email to ${pending.pending_count} employee(s) who have not submitted the week of ${pending.week_start}?`)) return;
    try {
      setNudging(true);
      await api.post("/timesheets/weeks/nudge-pending");
      toast.success(`Reminder dispatched to ${pending.pending_count} employee(s)`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setNudging(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <PageHeader overline={`${company.shortName} Operations`} title="Timesheet Management"
        subtitle="Live view of today's attendance, hours, and approvals."
        actions={<Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none border-[#E5E5E5] w-44" data-testid="dash-date" />} />

      {user?.role === "admin" && <ClockWidget dailyHours={stats.daily_hours || 8} />}

      {approvals.total > 0 && (
        <button onClick={() => navigate("/admin/approvals")} data-testid="dash-approvals-banner"
          className="w-full mb-4 p-4 border border-[#1F4E8C] bg-[#1F4E8C]/5 hover:bg-[#1F4E8C]/10 transition-colors text-left flex items-center gap-4">
          <span className="min-w-[44px] h-11 px-2 bg-[#FF5500] text-white font-head font-bold text-lg grid place-items-center rounded-full" data-testid="dash-approvals-count">
            {approvals.total > 99 ? "99+" : approvals.total}
          </span>
          <div className="flex-1">
            <div className="font-head font-semibold text-sm text-[#1F4E8C]">Pending approvals waiting on you</div>
            <div className="text-xs text-neutral-600">{approvals.leave} leave request(s) · {approvals.timesheets} timesheet(s) — click to review</div>
          </div>
          <span className="text-[#1F4E8C] font-semibold text-sm whitespace-nowrap">Review →</span>
        </button>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Kpi testid="kpi-employees" label="Total Employees" value={stats.total_employees ?? "—"} accent />
        <Kpi testid="kpi-present" label="Present" value={stats.present_today ?? "—"} hint={`${stats.absent_today || 0} absent`} />
        <Kpi testid="kpi-leave" label="On Leave" value={stats.on_leave ?? "—"} />
        <Kpi testid="kpi-projects" label="Active Projects" value={stats.active_projects ?? "—"} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi testid="kpi-hours" label="Hours Today" value={stats.total_hours ?? "—"} />
        <Kpi testid="kpi-billable" label="Billable" value={stats.billable_hours ?? "—"} />
        <Kpi testid="kpi-overtime" label="Overtime" value={stats.overtime_hours ?? "—"} accent />
        <Kpi testid="kpi-pending" label="Pending Approvals" value={(stats.pending_timesheets || 0) + (stats.pending_leaves || 0)} hint={`${stats.pending_timesheets || 0} TS · ${stats.pending_leaves || 0} Leave`} />
      </div>

      {pending.pending_count > 0 && (
        <div className="mb-6 p-4 border border-amber-400 bg-amber-50" data-testid="pending-tile">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow text-amber-800">Weekly submissions due</div>
              <div className="font-head text-2xl font-semibold text-amber-900 mt-1">
                {pending.pending_count} of {pending.total_active} employees haven't submitted last week
              </div>
              <div className="text-xs text-amber-800 mt-1">Week of {pending.week_start} → {pending.week_end}</div>
              {pending.pending.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1 max-w-[700px]">
                  {pending.pending.slice(0, 10).map((p) => (
                    <span key={p.id} className="px-2 py-0.5 bg-white border border-amber-300 text-xs text-amber-900" title={p.email}>
                      {p.name}
                    </span>
                  ))}
                  {pending.pending.length > 10 && (
                    <span className="px-2 py-0.5 text-xs text-amber-800">+{pending.pending.length - 10} more</span>
                  )}
                </div>
              )}
            </div>
            <button onClick={nudge} disabled={nudging} data-testid="nudge-pending-btn"
              className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] disabled:opacity-60 text-white px-5 h-11 font-semibold text-sm whitespace-nowrap">
              {nudging ? "Dispatching…" : "Nudge them now →"}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="eyebrow text-neutral-500">Analytics</h2>
          {charts.from_date && (
            <div className="text-xs text-neutral-500 mt-1" data-testid="analytics-range">
              {charts.from_date} → {charts.to_date}
              {charts.fallback && <span className="ml-2 text-[#FF5500]" data-testid="analytics-fallback-note">No timesheets in the selected period — showing the most recent activity</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="border border-neutral-300 text-sm px-2 py-1 bg-white" data-testid="analytics-period-select">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid="include-completed-toggle">
          <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)}
            className="accent-[#FF5500] w-4 h-4" data-testid="include-completed-checkbox" />
          <span className="text-neutral-700">Include completed projects</span>
          <span className="text-xs text-neutral-400">(historical review)</span>
        </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Section title={`Daily Working Hours (${days}d)`}>
          <ChartBox empty={!charts.daily_trend.length} height={240} id="daily">
            <LineChart data={charts.daily_trend}>
              <CartesianGrid stroke="#F0F0EE" />
              <XAxis dataKey="date" stroke="#888" fontSize={11} />
              <YAxis stroke="#888" fontSize={11} />
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Line type="monotone" dataKey="hours" stroke="#FF5500" strokeWidth={2} dot={{ r: 3, fill: "#1F4E8C" }} />
            </LineChart>
          </ChartBox>
        </Section>
        <Section title="Project Hours">
          <ChartBox empty={!charts.project_hours.length} height={240} id="project-hours">
            <BarChart data={charts.project_hours}>
              <CartesianGrid stroke="#F0F0EE" />
              <XAxis dataKey="project" stroke="#888" fontSize={10} angle={-15} textAnchor="end" height={60} />
              <YAxis stroke="#888" fontSize={11} />
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Bar dataKey="hours" fill="#1F4E8C" />
            </BarChart>
          </ChartBox>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <Section title="Employee Hours">
          <ChartBox empty={!charts.employee_hours.length} height={260} id="employee-hours">
            <BarChart data={charts.employee_hours} layout="vertical">
              <CartesianGrid stroke="#F0F0EE" />
              <XAxis type="number" stroke="#888" fontSize={11} />
              <YAxis dataKey="employee" type="category" stroke="#888" fontSize={11} width={100} />
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Bar dataKey="hours" fill="#FF5500" />
            </BarChart>
          </ChartBox>
        </Section>
        <Section title="Project Distribution">
          <ChartBox empty={!charts.project_hours.length} height={260} id="distribution">
            <PieChart>
              <Pie data={charts.project_hours} dataKey="hours" nameKey="project" outerRadius={95} innerRadius={55}>
                {charts.project_hours.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ border: "1px solid #111", borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ChartBox>
        </Section>
      </div>

      {(() => {
        const atRisk = (projects || []).filter((p) => p.status !== "Cancelled" && p.status !== "Completed" && Number(p.utilization_pct) >= 80);
        const critical = atRisk.filter((p) => Number(p.utilization_pct) >= 100).length;
        const warning = atRisk.length - critical;
        return (
          <Section
            title="Project Health"
            right={
              <div className="flex items-center gap-2 text-xs">
                <span className="eyebrow">Flagged</span>
                <span data-testid="health-count-warning" className="inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> {warning} at risk
                </span>
                <span data-testid="health-count-critical" className="inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-700 px-2 py-0.5 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600" /> {critical} over budget
                </span>
              </div>
            }
            className="mb-4"
          >
            {atRisk.length === 0 ? (
              <div className="text-sm text-neutral-500 py-4 text-center" data-testid="health-empty">
                All projects within healthy utilization. Nothing to flag.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {atRisk
                  .sort((a, b) => (b.utilization_pct || 0) - (a.utilization_pct || 0))
                  .map((p) => {
                    const h = healthFromPct(p.utilization_pct);
                    const pct = Math.min(130, Math.max(0, p.utilization_pct || 0));
                    return (
                      <div key={p.id} data-testid={`health-card-${p.project_id}`}
                        className={`border p-4 ${h.tier === "critical" ? "border-red-300 bg-red-50/40" : "border-amber-300 bg-amber-50/40"}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="eyebrow">{p.project_id}</div>
                            <div className="font-head font-semibold text-base mt-1 leading-tight">{p.name}</div>
                            <div className="text-xs text-neutral-500 mt-0.5">{p.client} · {p.manager}</div>
                          </div>
                          <HealthBadge pct={p.utilization_pct} />
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-black/5 text-xs">
                          <div><div className="eyebrow text-[10px]">Budget</div><div className="font-head font-semibold text-sm">{p.budgeted_hours}h</div></div>
                          <div><div className="eyebrow text-[10px]">Used</div><div className="font-head font-semibold text-sm">{p.actual_hours}h</div></div>
                          <div>
                            <div className="eyebrow text-[10px]">Left</div>
                            <div className={`font-head font-semibold text-sm ${p.remaining_hours < 0 ? "text-red-600" : ""}`}>{p.remaining_hours}h</div>
                          </div>
                        </div>
                        <div className="relative h-1.5 bg-neutral-200/70 mt-3">
                          <div className="absolute inset-y-0 left-0" style={{ width: `${Math.min(100, pct)}%`, background: h.dot }} />
                          {pct > 100 && (
                            <div className="absolute inset-y-0 right-0 bg-red-600" style={{ width: `${Math.min(30, pct - 100)}%` }} />
                          )}
                          <div className="absolute inset-y-0 border-l border-neutral-400" style={{ left: "80%" }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </Section>
        );
      })()}

      <Section title={`Today's Activity — ${date}`}>
        <table className="w-full table-dense">
          <thead>
            <tr className="border-b-2 border-[#1F4E8C] text-left">
              <th className="eyebrow">Employee</th><th className="eyebrow">Dept</th>
              <th className="eyebrow">In</th><th className="eyebrow">Out</th>
              <th className="eyebrow">Hours</th><th className="eyebrow">Projects</th>
              <th className="eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {activity.map((a) => (
              <tr key={a.user_id} className="border-b border-[#F0F0EE]">
                <td className="font-medium">{a.name}</td>
                <td className="text-neutral-500">{a.department}</td>
                <td>{fmtTime(a.clock_in)}</td>
                <td>{fmtTime(a.clock_out)}</td>
                <td className="font-semibold">{a.total_hours || 0}h</td>
                <td>{a.projects_count}</td>
                <td><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
